import React, { useState } from "react";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import styled from "@emotion/styled";
import { useIsMutating } from "@tanstack/react-query";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import EmptyState from "components/ui/empty_state/EmptyState";
import ConfirmModal from "components/ui/modal/ConfirmModal";
import toast from "components/ui/toast/Toast";
import Tooltip from "components/ui/tooltip/Tooltip";
import {
  AI_QUICK_PROMPTS_MUTATION_KEY,
  useDeleteAiQuickPrompt,
  useReorderAiQuickPrompts,
  useResetAiQuickPrompts,
} from "lib/hooks/api/ai_quick_prompts/useAiQuickPromptMutations";
import { AiQuickPrompt, useAiQuickPrompts } from "lib/hooks/api/ai_quick_prompts/useAiQuickPrompts";
import { EventNames, useTracking } from "lib/tracking";
import { AiQuickPromptChatType, AiQuickPromptScope } from "lib/types/graphQLEnums";
import { LoadingState } from "../../app_layout/LoadingState";
import AddOrEditQuickPromptDrawer from "./AddOrEditQuickPromptDrawer";
import DeleteQuickPromptModal from "./DeleteQuickPromptModal";
import QuickPromptRow from "./QuickPromptRow";

// Matches the backend's limit
export const MAX_QUICK_PROMPTS = 20;

// Keeps the prompt after the dialog closes, so its content stays put while it animates out
type DialogState = { open: boolean; quickPrompt?: AiQuickPrompt };

type Props = {
  chatType: AiQuickPromptChatType;
  // Lays out the title and description around the list's actions (add and reset)
  renderHeader: (actions: React.ReactNode) => React.ReactNode;
  // Space between the header and the list
  gap?: number;
};

/**
 * Lets the team add, edit, delete, reorder and reset its quick prompts for an AI chat. Each change
 * saves straight away. Used by the settings page and the assistant's drawer.
 */
export default function QuickPromptsEditor({
  chatType,
  renderHeader,
  gap = 48,
}: Props): JSX.Element {
  const { logEvent } = useTracking();
  const { data, isLoading, isError } = useAiQuickPrompts(chatType);
  const quickPrompts = data?.prompts;
  // One change at a time: each builds on the list the one before saved, and a team's first change
  // gives every prompt a new id
  const isSaving = useIsMutating({ mutationKey: AI_QUICK_PROMPTS_MUTATION_KEY }) > 0;
  // A new key each time it opens gives the form a fresh start
  const [formDrawer, setFormDrawer] = useState<DialogState & { key: number }>({
    open: false,
    key: 0,
  });
  const [deleteDialog, setDeleteDialog] = useState<DialogState>({ open: false });
  const [isResetOpen, setIsResetOpen] = useState(false);

  const onError = () => toast.error("Unable to update your quick prompts; please try again");
  const { mutate: deletePrompt } = useDeleteAiQuickPrompt({ onError });
  const { mutate: reorderPrompts } = useReorderAiQuickPrompts({ onError });
  const { mutate: resetPrompts } = useResetAiQuickPrompts({
    onError: () => toast.error("Unable to reset your quick prompts; please try again"),
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  if (isLoading) {
    return <LoadingState title="Loading quick prompts" description="" />;
  }

  if (isError || !quickPrompts) {
    return (
      <EmptyState
        heading="Sorry, something has gone wrong"
        description="If the error persists, please contact support"
        type="loadError"
        size="large"
      />
    );
  }

  const isCustomised = quickPrompts.some(({ scope }) => scope !== AiQuickPromptScope.Default);

  const openForm = (quickPrompt?: AiQuickPrompt) =>
    setFormDrawer(({ key }) => ({ open: true, quickPrompt, key: key + 1 }));

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const activeIndex = quickPrompts.findIndex(({ id }) => id === active.id);
    const overIndex = quickPrompts.findIndex(({ id }) => id === over.id);
    if (activeIndex === -1 || overIndex === -1) return;

    const ids = arrayMove(quickPrompts, activeIndex, overIndex).map(({ id }) => id);
    reorderPrompts(
      { input: { chatType, ids } },
      {
        onSuccess: () => {
          toast.success("Quick prompts updated");
          logEvent(EventNames.quickPromptsReordered);
        },
      },
    );
  };

  const handleDelete = ({ id }: AiQuickPrompt) =>
    deletePrompt(
      { input: { chatType, id } },
      {
        onSuccess: () => {
          toast.success("Quick prompts updated");
          logEvent(EventNames.quickPromptDeleted);
        },
      },
    );

  const handleReset = () =>
    resetPrompts(
      { input: { chatType } },
      {
        onSuccess: () => {
          toast.success("Quick prompts reset to the defaults");
          logEvent(EventNames.quickPromptsReset);
        },
      },
    );

  const isAtLimit = quickPrompts.length >= MAX_QUICK_PROMPTS;
  const addButton = (
    <Button
      buttonType="secondary"
      icon="plus"
      disabled={isAtLimit || isSaving}
      onClick={() => openForm()}
    >
      Prompt
    </Button>
  );
  const actions = (
    <>
      {isCustomised && (
        <Button buttonType="tertiary" disabled={isSaving} onClick={() => setIsResetOpen(true)}>
          Reset to defaults
        </Button>
      )}
      {isAtLimit ? (
        <Tooltip content={`You can have up to ${MAX_QUICK_PROMPTS} quick prompts`}>
          <span>{addButton}</span>
        </Tooltip>
      ) : (
        addButton
      )}
    </>
  );

  return (
    <Flex column gap={gap} width="100%" alignItems="stretch">
      {renderHeader(actions)}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={quickPrompts.map(({ id }) => id)}
          strategy={verticalListSortingStrategy}
        >
          <PromptsContainer aria-label="Quick prompts">
            {quickPrompts.map((quickPrompt) => (
              <QuickPromptRow
                key={quickPrompt.id}
                id={quickPrompt.id}
                name={quickPrompt.name}
                prompt={quickPrompt.prompt}
                canDelete={quickPrompts.length > 1}
                disabled={isSaving}
                onEdit={() => openForm(quickPrompt)}
                onDelete={() => setDeleteDialog({ open: true, quickPrompt })}
              />
            ))}
          </PromptsContainer>
        </SortableContext>
      </DndContext>

      <AddOrEditQuickPromptDrawer
        key={formDrawer.key}
        chatType={chatType}
        open={formDrawer.open}
        quickPrompt={formDrawer.quickPrompt}
        isCustomised={isCustomised}
        onClose={() => setFormDrawer((state) => ({ ...state, open: false }))}
      />

      <DeleteQuickPromptModal
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog((state) => ({ ...state, open }))}
        label={deleteDialog.quickPrompt?.name ?? deleteDialog.quickPrompt?.prompt ?? ""}
        onDelete={() => {
          if (deleteDialog.quickPrompt) handleDelete(deleteDialog.quickPrompt);
        }}
      />

      <ConfirmModal
        open={isResetOpen}
        onOpenChange={setIsResetOpen}
        message={
          <Flex column gap={8}>
            <Text h3>Reset to the default quick prompts?</Text>
            <Text>
              This replaces your team's quick prompts with the defaults, for everyone in your team.
            </Text>
          </Flex>
        }
        confirmLabel="Reset"
        onConfirm={handleReset}
      />
    </Flex>
  );
}

const PromptsContainer = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  borderTop: `1px solid ${theme.colors.sysBorderDecorative}`,
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
}));
